import { SpeechSegment } from '../../../services/api.service';
import { canonicalizeSpeakerDisplayName } from '../../../utils/speaker-display-names';

export type MeetingPipelineKind = 'deepgram' | 'pyannote' | 'fast' | 'video' | 'other';

export type TranscriptionImproveStats = {
  speakerCount: number;
  segmentCount: number;
  unidentifiedSpeakerCount: number;
};

export type TranscriptionImproveMode = {
  kind: MeetingPipelineKind;
  showPyannote: boolean;
  showRetranscribe: boolean;
  showReprocessDeepgram: boolean;
  showProtocolOnly: boolean;
  description: string;
};

const GENERIC_SPEAKER = /^SPEAKER[_\s-]?\d+$/i;
const GENERIC_DISPLAY = /^(?:спикер|speaker)[_\s-]?\d+$/i;

export function normalizeMeetingPipeline(raw?: string | null): MeetingPipelineKind {
  const p = (raw || '').toLowerCase();
  if (p === 'deepgram') return 'deepgram';
  if (p === 'video') return 'video';
  if (p === 'pyannote' || p === 'diarization') return 'pyannote';
  if (p === 'fast') return 'fast';
  return 'other';
}

export function isIdentifiedSpeaker(seg: SpeechSegment): boolean {
  const label = (seg.speaker_label || '').trim();
  const display = canonicalizeSpeakerDisplayName((seg.speaker_display || seg.speaker_name || '').trim());
  if (display && !GENERIC_DISPLAY.test(display) && !/^unknown$/i.test(display)) {
    return true;
  }
  if (seg.speaker_name && !GENERIC_DISPLAY.test(seg.speaker_name)) {
    return true;
  }
  if (label && !GENERIC_SPEAKER.test(label)) {
    return true;
  }
  return false;
}

export function computeTranscriptionImproveStats(segments: SpeechSegment[]): TranscriptionImproveStats {
  const speakerKeys = new Set<string>();
  const unidentifiedKeys = new Set<string>();

  segments.forEach(seg => {
    const label = seg.speaker_label || '';
    const display = canonicalizeSpeakerDisplayName(seg.speaker_display || seg.speaker_name || '');
    const key = label || display || 'unknown';
    speakerKeys.add(key);
    if (!isIdentifiedSpeaker(seg)) {
      unidentifiedKeys.add(key);
    }
  });

  return {
    speakerCount: speakerKeys.size,
    segmentCount: segments.length,
    unidentifiedSpeakerCount: unidentifiedKeys.size,
  };
}

export function getTranscriptionImproveMode(pipeline?: string | null): TranscriptionImproveMode {
  const kind = normalizeMeetingPipeline(pipeline);

  switch (kind) {
    case 'deepgram':
      return {
        kind,
        showPyannote: true,
        showRetranscribe: true,
        showReprocessDeepgram: true,
        showProtocolOnly: true,
        description:
          'Базовый режим часто даёт 2–3 спикера на совещаниях. Чтобы разделить больше голосов — «Спикеры + протокол (Pyannote)». '
          + 'Ретранскрибация улучшает текст по текущим сегментам, но число спикеров не меняет.',
      };
    case 'video':
      return {
        kind,
        showPyannote: true,
        showRetranscribe: true,
        showReprocessDeepgram: true,
        showProtocolOnly: true,
        description:
          'Видео: диаризация и транскрипция за один проход. Для большего числа спикеров — Pyannote. Ретранскрибация — только текст по уже выделенным сегментам.',
      };
    case 'fast':
      return {
        kind,
        showPyannote: true,
        showRetranscribe: false,
        showReprocessDeepgram: false,
        showProtocolOnly: true,
        description:
          'Быстрый режим без разделения по спикерам. Для диаризации — Pyannote (10–20 мин).',
      };
    case 'pyannote':
      return {
        kind,
        showPyannote: true,
        showRetranscribe: true,
        showReprocessDeepgram: false,
        showProtocolOnly: true,
        description:
          'Pyannote (локально). Полное улучшение пересчитает спикеров; быстрое — только текст сегментов и протокол.',
      };
    default:
      return {
        kind,
        showPyannote: true,
        showRetranscribe: true,
        showReprocessDeepgram: false,
        showProtocolOnly: true,
        description:
          'Для имён спикеров — голосовые образцы в «Спикеры» или полное улучшение (Pyannote).',
      };
  }
}
